import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { RiskWarning } from '@/types/trading';

interface RiskWarningDialogProps {
  warnings: RiskWarning[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function RiskWarningDialog({
  warnings,
  onConfirm,
  onCancel,
  isLoading,
}: RiskWarningDialogProps) {
  const hasBlockingWarnings = warnings.some((w) => w.severity === 'block');

  return (
    <AlertDialog
      open={warnings.length > 0}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-yellow-600">
            <AlertTriangle className="h-5 w-5" />
            Risk Warning
          </AlertDialogTitle>
          <AlertDialogDescription>
            {hasBlockingWarnings
              ? 'This order cannot be placed due to blocking risk violations.'
              : 'You can proceed with this order despite the warnings by clicking "Override & Continue".'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          {warnings.map((warning, i) => (
            <div
              key={`${warning.rule}-${i}`}
              className={`p-3 rounded text-sm ${
                warning.severity === 'block'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
              }`}
            >
              <p className="font-medium">{warning.rule}</p>
              <p>{warning.message}</p>
            </div>
          ))}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          {!hasBlockingWarnings && (
            <AlertDialogAction
              onClick={onConfirm}
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? 'Processing...' : 'Override & Continue'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RiskWarningDialog;
